import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ChevronDown, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  getPrimaryQuickAction,
  getQuickActions,
  getSecondaryQuickActions,
} from '@/components/header/header-config';
import type { HeaderMode } from '@/types/header';

interface HeaderQuickActionsProps {
  mode: HeaderMode;
  layout?: 'toolbar' | 'panel';
  showPrimaryButton?: boolean;
  includePrimaryInMenu?: boolean;
  compact?: boolean;
  onAction?: () => void;
  className?: string;
}

export function HeaderQuickActions({
  mode,
  layout = 'toolbar',
  showPrimaryButton = false,
  includePrimaryInMenu = false,
  compact = false,
  onAction,
  className,
}: HeaderQuickActionsProps) {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const { t: tAdmin } = useTranslation('admin');

  const primaryAction = useMemo(() => getPrimaryQuickAction(mode), [mode]);
  const secondaryActions = useMemo(() => getSecondaryQuickActions(mode), [mode]);
  const actions = useMemo(
    () => (includePrimaryInMenu ? getQuickActions(mode) : secondaryActions),
    [includePrimaryInMenu, mode, secondaryActions],
  );

  const translate = (namespace: 'common' | 'navigation' | 'admin', key: string): string => {
    if (namespace === 'navigation') return tNav(key);
    if (namespace === 'admin') return tAdmin(key);
    return tCommon(key);
  };

  const go = (path: string) => {
    onAction?.();
    navigate(path);
  };

  if (layout === 'panel') {
    if (actions.length === 0) return null;

    return (
      <div className={cn('grid gap-2', className)}>
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              type="button"
              onClick={() => go(action.path)}
              className="flex w-full items-start gap-3 rounded-md border-[length:var(--border-brutal)] border-charcoal-950 bg-white px-3 py-2 text-left shadow-sm hover:bg-neutral-50 dark:hover:bg-charcoal-700"
            >
              <span className="mt-0.5 rounded-md border border-charcoal-200 bg-neutral-50 dark:bg-charcoal-700 p-1.5">
                <Icon className="size-4 text-charcoal-700" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-charcoal-900">
                  {translate(action.label.namespace, action.label.key)}
                </span>
                {action.description && (
                  <span className="block text-xs text-charcoal-500">
                    {translate(action.description.namespace, action.description.key)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  if (!primaryAction) return null;
  const PrimaryIcon = primaryAction.icon;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showPrimaryButton && (
        <Button
          type="button"
          size="sm"
          onClick={() => go(primaryAction.path)}
          className="hidden lg:inline-flex"
        >
          <PrimaryIcon className="size-4" />
          {translate(primaryAction.label.namespace, primaryAction.label.key)}
        </Button>
      )}

      {actions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="sm" variant="outline" className="gap-1.5">
              <Zap className="size-4" />
              <span className={compact ? 'hidden xl:inline' : 'hidden sm:inline'}>
                {tCommon('header_quick_actions')}
              </span>
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>{tCommon('header_quick_actions')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <DropdownMenuItem key={action.id} onSelect={() => go(action.path)}>
                  <Icon className="size-4" />
                  <span>{translate(action.label.namespace, action.label.key)}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
